import { NO_DATA_AVAILABLE } from '@/config/ui-messages'

const DEFAULT_MESSAGE = 'Technical Error'

export type ApiErrorMessageKind = 'technical' | 'unauthorized'

/**
 * Central mapping: status code -> error message kind.
 * Keep this as the single source of truth for special-case UI messaging.
 */
export const STATUS_CODE_TO_ERROR_MESSAGE_KIND: Readonly<
  Record<number, ApiErrorMessageKind>
> = {
  500: 'technical',
  401: 'unauthorized',
  // 404: 'unauthorized',
  // 403: 'unauthorized',
  // 422: 'unauthorized',
} as const

function getMessageKindFromStatus(
  status: number | undefined
): ApiErrorMessageKind | undefined {
  if (status == null) return undefined
  return STATUS_CODE_TO_ERROR_MESSAGE_KIND[status]
}

function getStatusFromError(error: unknown): number | undefined {
  if (error == null || typeof error !== 'object' || !('response' in error))
    return undefined
  const res = (error as { response?: { status?: number } }).response
  return res?.status
}

function isAxiosResponseData(value: unknown): value is {
  detail?: unknown
  message?: string
  error?: unknown
  title?: string
} {
  return value !== null && typeof value === 'object'
}

function isValidationItem(
  obj: unknown
): obj is { msg?: string; message?: string } {
  return (
    obj !== null &&
    typeof obj === 'object' &&
    ('msg' in obj || 'message' in obj)
  )
}

function stringifyDetail(detail: unknown): string {
  if (detail == null) return ''
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    const parts = detail
      .filter(isValidationItem)
      .map((item) => item.msg ?? item.message ?? '')
      .filter(Boolean)
    return parts.length > 0 ? parts.join('. ') : DEFAULT_MESSAGE
  }
  if (typeof detail === 'object' && detail !== null) {
    const d = detail as {
      detail?: unknown
      message?: string
      msg?: string
      errors?: Array<{ reason?: string; message?: string }>
    }
    // Support RFC7807-style payloads where `detail` contains the user-facing message.
    if (typeof d.detail === 'string' && d.detail) return d.detail
    const main = d.message ?? d.msg ?? ''
    const firstErr = Array.isArray(d.errors) ? d.errors[0] : undefined
    const firstError = firstErr && (firstErr.reason ?? firstErr.message)
    if (main && firstError) return `${main}. ${firstError}`
    if (main) return main
    if (firstError) return firstError
  }
  return ''
}

export function getSafeErrorMessage(
  error: unknown,
  fallback: string = DEFAULT_MESSAGE
): string {
  const status = getStatusFromError(error)
  const kind = getMessageKindFromStatus(status)

  if (kind === 'technical') return DEFAULT_MESSAGE
  if (kind === 'unauthorized') return NO_DATA_AVAILABLE

  if (error == null) return fallback
  if (typeof error === 'string') return error || fallback

  // Prefer response body over Error.message (Axios sets message to e.g. "Request failed with status code 409")
  if (typeof error === 'object' && 'response' in error) {
    const res = (error as { response?: { data?: unknown; status?: number } })
      .response
    if (res?.data && isAxiosResponseData(res.data)) {
      const data = res.data
      const fromDetail = stringifyDetail(data.detail)
      if (fromDetail) return fromDetail
      if (typeof data.message === 'string') return data.message
      if (typeof data.error === 'string') return data.error
      if (data.error && typeof data.error === 'object') {
        const err = data.error as { detail?: unknown; message?: string }
        const fromErrDetail = stringifyDetail(err.detail)
        if (fromErrDetail) return fromErrDetail
        if (typeof err.message === 'string') return err.message
      }
      if (typeof data.title === 'string') return data.title
    }
  }

  if (error instanceof Error) {
    if (error.message && typeof error.message === 'string') return error.message
    return fallback
  }

  return fallback
}

/**
 * Table-only messaging:
 * - 500 -> Technical Error
 * - everything else -> No data available
 *
 * This is intentionally NOT the same as toast messaging (hooks can still show API messages).
 */
export function getSafeTableErrorMessage(error: unknown): string {
  const status = getStatusFromError(error)
  return status === 500 ? DEFAULT_MESSAGE : NO_DATA_AVAILABLE
}

/** Show technical error toast only for 500 (Internal Server Error). */
export function shouldShowTechnicalErrorToast(
  status: number | undefined
): boolean {
  return getMessageKindFromStatus(status) === 'technical'
}

/** Show "Unauthorized access" toast for 422, 404, 403. */
export function shouldShowUnauthorizedToast(
  status: number | undefined
): boolean {
  return getMessageKindFromStatus(status) === 'unauthorized'
}

/** Flag set on the error by api-client when the technical error toast was already shown. Use to avoid showing a second (white) toast. */
export const TECHNICAL_ERROR_TOAST_SHOWN = '__technicalErrorToastShown'

/** True if the api-client already showed the error toast for this error (500 technical or 422/404/403 unauthorized). */
export function wasTechnicalErrorToastShown(error: unknown): boolean {
  if (error == null || typeof error !== 'object') return false
  return (
    (error as Record<string, unknown>)[TECHNICAL_ERROR_TOAST_SHOWN] === true
  )
}
